import { MATERIAL_TYPE_NONE, materialListSchema, type MaterialListParams } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { CatalogItemPopup } from '../components/catalog/CatalogItemPopup.js'
import { Icon } from '../components/Icon.js'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Pagination,
  SearchBox,
  SkeletonRows,
  Toolbar,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { money } from '../lib/format.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'
import { MaterialTypesDialog } from './catalog/MaterialTypesDialog.js'

type Material = RouterOutputs['catalog']['materials']['items'][number]
type MaterialType = RouterOutputs['catalog']['materialTypes'][number]
type View = MaterialListParams['view']

/**
 * The catalog, in two halves. ADR 022.
 *
 * **Our catalog** is the short list the office keeps and the quote picker
 * offers. **From ServiceM8** is everything the import brought across — about
 * 1,272 rows of a decade's history — and it is where kept items are chosen
 * from. Adding one moves the same row across rather than copying it, so the
 * next sync still reaches its price.
 *
 * The type is the office's own list, edited under Types, and it is both the
 * filter and a column that can be changed on the row or for a whole selection.
 */
export function Materials() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const editable = can('CATALOG_EDIT')
  const [params, setParams] = useUrlState(materialListSchema)
  const [typesOpen, setTypesOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Material | null>(null)

  const { data, isPending, error, refetch } = useQuery(trpc.catalog.materials.queryOptions(params))
  const types = useQuery(trpc.catalog.materialTypes.queryOptions({ includeArchived: true }))

  /**
   * Ticked rows, scoped to the page they were ticked on.
   *
   * A selection that survived a tab change or a new search would act on rows
   * nobody can see any more, so the key drops it the moment the list changes.
   */
  const listKey = [params.view, params.page, params.q, params.typeId, params.sort, params.dir].join('|')
  const [selection, setSelection] = useState<{ key: string; ids: Set<string> }>({
    key: listKey,
    ids: new Set(),
  })
  const selected = selection.key === listKey ? selection.ids : new Set<string>()
  const setSelected = (ids: Set<string>) => setSelection({ key: listKey, ids })

  const invalidate = () => void queryClient.invalidateQueries(trpc.catalog.pathFilter())

  const curate = useMutation(
    trpc.catalog.setMaterialsCurated.mutationOptions({
      onSuccess: () => {
        setSelected(new Set())
        invalidate()
      },
    }),
  )
  const retype = useMutation(trpc.catalog.setMaterialsType.mutationOptions({ onSuccess: invalidate }))

  const activeTypes = useMemo(
    () => (types.data ?? []).filter((type) => type.archivedAt === null),
    [types.data],
  )

  const items = data?.items ?? []
  const allTicked = items.length > 0 && items.every((item) => selected.has(item.id))
  const kept = params.view === 'kept'
  const mutationError = curate.error ?? retype.error

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  return (
    <Page width="xwide">
      <PageHeader
        title="Catalog"
        actions={
          editable ? (
            <Button variant="primary" className="rounded-full" onClick={() => setCreating(true)}>
              New item
            </Button>
          ) : null
        }
      />

      <Card>
        <Toolbar>
          <ViewTabs
            view={params.view}
            counts={data?.counts}
            onChange={(view) => setParams({ view })}
          />

          <SearchBox
            value={params.q ?? ''}
            onChange={(q) => setParams({ q: q || undefined }, { replace: true })}
            placeholder="Search catalog…"
          />

          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <span>Type</span>
            <select
              aria-label="Filter by type"
              value={params.typeId ?? ''}
              onChange={(event) => setParams({ typeId: event.target.value || undefined })}
              className="h-[var(--control-sm)] rounded-md border border-ink-200 bg-white px-2 text-xs text-ink-800"
            >
              <option value="">All types</option>
              <option value={MATERIAL_TYPE_NONE}>No type</option>
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.archivedAt ? `${type.name} (retired)` : type.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <span>Sort</span>
            <select
              aria-label="Sort by"
              value={params.sort}
              onChange={(event) =>
                setParams({ sort: event.target.value as MaterialListParams['sort'] })
              }
              className="h-[var(--control-sm)] rounded-md border border-ink-200 bg-white px-2 text-xs text-ink-800"
            >
              <option value="name">Name</option>
              <option value="type">Type</option>
              <option value="code">Code</option>
            </select>
          </label>

          {editable ? (
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setTypesOpen(true)}>
              Types
            </Button>
          ) : null}
        </Toolbar>

        {editable && selected.size > 0 ? (
          <div
            role="region"
            aria-label="Selected items"
            className="flex flex-wrap items-center gap-2 border-b border-brand-200 bg-brand-50 px-[var(--row-pad-x)] py-2 text-xs"
          >
            <span className="font-medium text-ink-900">{selected.size} selected</span>

            <Button
              size="sm"
              variant="primary"
              loading={curate.isPending}
              onClick={() => curate.mutate({ ids: [...selected], curated: !kept })}
            >
              {kept ? `Remove ${selected.size} from our catalog` : `Add ${selected.size} to our catalog`}
            </Button>

            <select
              aria-label="Set type of selected items"
              value=""
              disabled={retype.isPending}
              onChange={(event) => {
                const value = event.target.value
                if (!value) return
                retype.mutate({
                  ids: [...selected],
                  typeId: value === MATERIAL_TYPE_NONE ? null : value,
                })
              }}
              className="h-[var(--control-sm)] rounded-md border border-ink-200 bg-white px-2 text-xs text-ink-800"
            >
              <option value="">Set type…</option>
              <option value={MATERIAL_TYPE_NONE}>No type</option>
              {activeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>

            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        ) : null}

        {mutationError ? (
          <p
            role="alert"
            className="border-b border-red-200 bg-red-50 px-[var(--row-pad-x)] py-2 text-xs text-red-700"
          >
            {errorMessage(mutationError)}
          </p>
        ) : null}

        {error ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          kept && !params.q && !params.typeId ? (
            <EmptyState
              title="Nothing kept yet"
              description="Add items from the ServiceM8 tab. Only what is here is offered when a line is added to a quote."
            />
          ) : (
            <EmptyState title="Nothing matches that" description="Try another search or type." />
          )
        ) : (
          <ul className="divide-y divide-ink-100">
            {editable ? (
              <li className="flex items-center gap-3 bg-ink-50/60 px-[var(--row-pad-x)] py-1.5 text-xs text-ink-500">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allTicked}
                  onChange={() =>
                    setSelected(allTicked ? new Set() : new Set(items.map((item) => item.id)))
                  }
                />
                <span>Select all on this page</span>
              </li>
            ) : null}

            {items.map((material) => (
              <MaterialRow
                key={material.id}
                material={material}
                kept={kept}
                editable={editable}
                ticked={selected.has(material.id)}
                types={activeTypes}
                pending={curate.isPending}
                onTick={() => toggle(material.id)}
                onEdit={() => setEditing(material)}
                onType={(typeId) => retype.mutate({ ids: [material.id], typeId })}
                onMove={() => curate.mutate({ ids: [material.id], curated: !kept })}
              />
            ))}
          </ul>
        )}

        {data ? (
          <Pagination
            page={data.page}
            pageCount={data.pageCount}
            total={data.total}
            onChange={(page) => setParams({ page })}
          />
        ) : null}
      </Card>

      {editable ? (
        <CatalogItemPopup
          open={creating || editing !== null}
          item={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      ) : null}

      {editable ? (
        <MaterialTypesDialog
          open={typesOpen}
          types={types.data ?? []}
          onClose={() => setTypesOpen(false)}
          onChanged={invalidate}
        />
      ) : null}
    </Page>
  )
}

const VIEWS: Array<{ key: View; label: string; caption: string }> = [
  {
    key: 'kept',
    label: 'Our catalog',
    caption: 'The items offered when a line is added to a quote.',
  },
  {
    key: 'sm8',
    label: 'From ServiceM8',
    caption: 'Everything the import brought across. Add what you use to our catalog.',
  },
]

/** The board's segmented pill, as on the Jobs list. */
function ViewTabs({
  view,
  counts,
  onChange,
}: {
  view: View
  counts: Record<View, number> | undefined
  onChange: (view: View) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Catalog"
      className="flex w-full items-center gap-0.5 rounded-full bg-ink-100 p-0.5 sm:w-auto sm:shrink-0"
    >
      {VIEWS.map((entry) => {
        const selected = entry.key === view
        return (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={selected}
            title={entry.caption}
            onClick={() => onChange(entry.key)}
            className={cn(
              'flex min-h-[var(--control-sm)] flex-1 items-center justify-center gap-1.5 rounded-full px-3 text-xs whitespace-nowrap transition-colors sm:flex-none',
              selected
                ? 'bg-brand-800 font-semibold text-white'
                : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
            )}
          >
            {entry.label}
            <span
              className={cn('text-[11px] tabular-nums', selected ? 'text-white/70' : 'text-ink-400')}
            >
              {counts ? counts[entry.key].toLocaleString('en-AU') : ''}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function MaterialRow({
  material,
  kept,
  editable,
  ticked,
  types,
  pending,
  onTick,
  onEdit,
  onType,
  onMove,
}: {
  material: Material
  kept: boolean
  editable: boolean
  ticked: boolean
  /** Types that can be given. A retired one on this row is added back below. */
  types: MaterialType[]
  pending: boolean
  onTick: () => void
  onEdit: () => void
  onType: (typeId: string | null) => void
  onMove: () => void
}) {
  // Shown because a negative or zero margin on a catalog line is usually a
  // data-entry error, and it is invisible otherwise.
  const margin =
    material.unitPrice > 0
      ? Math.round(((material.unitPrice - material.unitCost) / material.unitPrice) * 100)
      : null

  // A retired type is off the list and still on this item. Without it as an
  // option the select would show the first entry and write it back on focus.
  const retired = material.type?.archivedAt ? material.type : null

  return (
    <li
      className={cn(
        'flex min-h-[var(--control-md)] items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50',
        ticked && 'bg-brand-50/60',
      )}
    >
      {editable ? (
        <input
          type="checkbox"
          aria-label={`Select ${material.name}`}
          checked={ticked}
          onChange={onTick}
        />
      ) : null}

      <span
        className="hidden w-24 shrink-0 truncate font-mono text-xs text-ink-500 sm:block"
        title={material.code ?? undefined}
      >
        {material.code ?? '—'}
      </span>

      {editable ? (
        <button
          type="button"
          title="Edit this item"
          onClick={onEdit}
          className="group min-w-0 flex-1 text-left"
        >
          <span className="block truncate text-sm font-medium text-ink-900 group-hover:underline">{material.name}</span>
          <span className="block truncate text-xs text-ink-500">
            per {material.unitOfMeasure}
            {material.supplier ? ` · ${material.supplier.name}` : ''}
          </span>
        </button>
      ) : (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink-900">{material.name}</span>
          <span className="block truncate text-xs text-ink-500">
            per {material.unitOfMeasure}
            {material.supplier ? ` · ${material.supplier.name}` : ''}
          </span>
        </span>
      )}

      {editable ? (
        <select
          aria-label={`Type of ${material.name}`}
          value={material.type?.id ?? ''}
          onChange={(event) => onType(event.target.value || null)}
          className={cn(
            'h-[var(--control-sm)] w-36 shrink-0 rounded-md border-0 bg-transparent px-1 text-xs',
            'hover:bg-white hover:ring-1 hover:ring-ink-200 focus:ring-2 focus:ring-brand-600',
            material.type ? 'text-ink-800' : 'text-ink-400',
          )}
        >
          <option value="">No type</option>
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
          {retired ? (
            <option value={retired.id}>{`${retired.name} (retired)`}</option>
          ) : null}
        </select>
      ) : material.type ? (
        <Badge className="shrink-0">{material.type.name}</Badge>
      ) : null}

      <span className="hidden w-20 shrink-0 text-right text-xs tabular-nums text-ink-500 sm:block">
        {money(material.unitCost)}
      </span>

      <span className="w-20 shrink-0 text-right text-sm tabular-nums text-ink-900">
        {money(material.unitPrice)}
      </span>

      {margin !== null && margin <= 0 ? (
        <Badge tone="red">{margin}%</Badge>
      ) : (
        <span className="hidden w-12 shrink-0 text-right text-xs tabular-nums text-ink-400 sm:block">
          {margin === null ? '' : `${margin}%`}
        </span>
      )}

      {editable ? (
        <Button size="sm" variant="ghost" aria-label={`Edit ${material.name}`} onClick={onEdit} className="shrink-0">
          Edit
        </Button>
      ) : null}

      {editable ? (
        <Button
          size="sm"
          variant={kept ? 'ghost' : 'secondary'}
          disabled={pending}
          aria-label={kept ? `Remove ${material.name} from our catalog` : `Add ${material.name} to our catalog`}
          title={
            kept
              ? 'Back to the ServiceM8 list. It stops being offered on quotes.'
              : 'Keep this, so it is offered when a line is added to a quote.'
          }
          icon={kept ? undefined : <Icon name="plus" className="size-3.5" />}
          onClick={onMove}
          className="w-20 shrink-0"
        >
          {kept ? 'Remove' : 'Add'}
        </Button>
      ) : null}
    </li>
  )
}
